// Categories that patients can add entries to (10 of 14)
export type UpdatableCategory =
  | 'emergencyContacts'
  | 'familyDoctor'
  | 'conditions'
  | 'allergies'
  | 'medications'
  | 'vaccineHistory'
  | 'surgicalHistory'
  | 'travelHistory'
  | 'familyHistory'
  | 'careTeam';

// Admin-only categories (not available for patient self-update)
export type AdminOnlyCategory =
  | 'personalIdentification'
  | 'demographics'
  | 'diagnosticHistory'
  | 'insurance';

// All categories (patient + admin)
export type AllCategory = UpdatableCategory | AdminOnlyCategory;

export interface CategoryField {
  key: string;
  label: string;
  type: 'text' | 'date';
  required?: boolean;
  placeholder?: string;
}

export interface CategorySchema {
  key: UpdatableCategory;
  label: string;
  fields: CategoryField[];
}

export interface AllCategorySchema {
  key: AllCategory;
  label: string;
  fields: CategoryField[];
}

export const UPDATABLE_CATEGORIES: CategorySchema[] = [
  {
    key: 'emergencyContacts',
    label: 'Emergency Contacts',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Full name' },
      { key: 'relationship', label: 'Relationship', type: 'text', placeholder: 'e.g. Spouse, Parent' },
      { key: 'phone', label: 'Phone', type: 'text', placeholder: 'Phone number' },
      { key: 'email', label: 'Email', type: 'text', placeholder: 'Email address' },
    ],
  },
  {
    key: 'familyDoctor',
    label: 'Family Doctor & Pharmacy',
    fields: [
      { key: 'role', label: 'Role', type: 'text', placeholder: 'e.g. Family Doctor, Pharmacy' },
      { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Provider name' },
      { key: 'address', label: 'Address', type: 'text', placeholder: 'Office address' },
      { key: 'phone', label: 'Phone', type: 'text', placeholder: 'Phone number' },
      { key: 'fax', label: 'Fax', type: 'text', placeholder: 'Fax number' },
    ],
  },
  {
    key: 'conditions',
    label: 'Active Medical Conditions',
    fields: [
      { key: 'condition', label: 'Condition', type: 'text', required: true, placeholder: 'Condition name' },
      { key: 'dateOfOnset', label: 'Date of Onset', type: 'date' },
      { key: 'recentReading', label: 'Recent Reading', type: 'text', placeholder: 'Latest reading or value' },
      { key: 'comments', label: 'Comments', type: 'text', placeholder: 'Additional notes' },
    ],
  },
  {
    key: 'allergies',
    label: 'Allergies & Adverse Reactions',
    fields: [
      { key: 'allergen', label: 'Allergen / Agent', type: 'text', required: true, placeholder: 'Allergen name' },
      { key: 'reactionType', label: 'Reaction Type', type: 'text', placeholder: 'e.g. Hives, Anaphylaxis' },
      { key: 'severity', label: 'Severity', type: 'text', placeholder: 'Mild, Moderate, or Severe' },
      { key: 'comments', label: 'Comments', type: 'text', placeholder: 'Additional notes' },
    ],
  },
  {
    key: 'medications',
    label: 'Medications',
    fields: [
      { key: 'name', label: 'Medication', type: 'text', required: true, placeholder: 'Medication name' },
      { key: 'dosageFrequency', label: 'Dosage / Frequency', type: 'text', placeholder: 'e.g. 10mg once daily' },
      { key: 'dateFirstDispensed', label: 'First Dispensed', type: 'date' },
      { key: 'dateLastDispensed', label: 'Last Dispensed', type: 'date' },
      { key: 'prescribingProvider', label: 'Prescriber', type: 'text', placeholder: 'Prescribing provider' },
    ],
  },
  {
    key: 'vaccineHistory',
    label: 'Vaccine History',
    fields: [
      { key: 'vaccine', label: 'Vaccine', type: 'text', required: true, placeholder: 'Vaccine name' },
      { key: 'dosage', label: 'Dosage', type: 'text', placeholder: 'Dose number or amount' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'provider', label: 'Provider', type: 'text', placeholder: 'Administering provider' },
      { key: 'source', label: 'Source', type: 'text', placeholder: 'e.g. Clinic, Pharmacy' },
    ],
  },
  {
    key: 'surgicalHistory',
    label: 'Surgical History & Hospitalization',
    fields: [
      { key: 'procedure', label: 'Procedure / Hospitalization', type: 'text', required: true, placeholder: 'Procedure name' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'comments', label: 'Comments', type: 'text', placeholder: 'Additional notes' },
    ],
  },
  {
    key: 'travelHistory',
    label: 'Travel History',
    fields: [
      { key: 'startDate', label: 'Start Date', type: 'date' },
      { key: 'endDate', label: 'End Date', type: 'date' },
      { key: 'cityRegion', label: 'City / Region', type: 'text', required: true, placeholder: 'City or region' },
      { key: 'country', label: 'Country', type: 'text', required: true, placeholder: 'Country' },
    ],
  },
  {
    key: 'familyHistory',
    label: 'Family History',
    fields: [
      { key: 'relation', label: 'Relation', type: 'text', required: true, placeholder: 'e.g. Mother, Father' },
      { key: 'condition', label: 'Medical Condition', type: 'text', required: true, placeholder: 'Condition' },
      { key: 'ageOfOnset', label: 'Age of Onset', type: 'text', placeholder: 'Age when diagnosed' },
      { key: 'provider', label: 'Provider', type: 'text', placeholder: 'Diagnosing provider' },
    ],
  },
  {
    key: 'careTeam',
    label: 'Care Team',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Provider name' },
      { key: 'specialty', label: 'Specialty', type: 'text', placeholder: 'e.g. Cardiology' },
      { key: 'address', label: 'Address', type: 'text', placeholder: 'Office address' },
      { key: 'phone', label: 'Phone', type: 'text', placeholder: 'Phone number' },
      { key: 'dateOfLastVisit', label: 'Last Visit', type: 'date' },
    ],
  },
];

export const ADMIN_ONLY_CATEGORIES: AllCategorySchema[] = [
  {
    key: 'personalIdentification',
    label: 'Personal Identification',
    fields: [
      { key: 'field', label: 'Field Name', type: 'text', required: true, placeholder: 'e.g. Full Name, Date of Birth' },
      { key: 'value', label: 'Value', type: 'text', required: true, placeholder: 'Field value' },
    ],
  },
  {
    key: 'demographics',
    label: 'Demographics',
    fields: [
      { key: 'field', label: 'Field Name', type: 'text', required: true, placeholder: 'e.g. Gender, Blood Type' },
      { key: 'value', label: 'Value', type: 'text', required: true, placeholder: 'Field value' },
    ],
  },
  {
    key: 'diagnosticHistory',
    label: 'Diagnostic History',
    fields: [
      { key: 'procedure', label: 'Procedure', type: 'text', required: true, placeholder: 'Procedure name' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'keyFinding', label: 'Key Finding', type: 'text', placeholder: 'Key finding or result' },
      { key: 'notes', label: 'Notes', type: 'text', placeholder: 'Additional notes' },
    ],
  },
  {
    key: 'insurance',
    label: 'Insurance & Care Planning',
    fields: [
      { key: 'field', label: 'Field Name', type: 'text', required: true, placeholder: 'e.g. Insurance Provider, Policy Number' },
      { key: 'value', label: 'Value', type: 'text', required: true, placeholder: 'Field value' },
    ],
  },
];

export const ALL_CATEGORIES: AllCategorySchema[] = [
  ...UPDATABLE_CATEGORIES,
  ...ADMIN_ONLY_CATEGORIES,
];

export const CATEGORY_IDENTITY_FIELDS: Record<AllCategory, string[]> = {
  emergencyContacts: ['name'],
  familyDoctor: ['name'],
  conditions: ['condition'],
  allergies: ['allergen'],
  medications: ['name'],
  vaccineHistory: ['vaccine'],
  surgicalHistory: ['procedure'],
  travelHistory: ['cityRegion', 'country'],
  familyHistory: ['relation', 'condition'],
  careTeam: ['name'],
  personalIdentification: ['field'],
  demographics: ['field'],
  diagnosticHistory: ['procedure'],
  insurance: ['field'],
};

export interface HealthUpdateAcknowledgement {
  confirmed: boolean;
  signedName: string;
  signedAt: string;
}

export interface HealthUpdate {
  updateId: string;
  category: AllCategory;
  data: Record<string, string>;
  username: string;
  submittedAt: string;
  acknowledgement: HealthUpdateAcknowledgement;
}

export interface ArchivedHealthUpdate extends HealthUpdate {
  archivedAt: string;
  replacedBy: string;
}

export interface HealthUpdateSubmitRequest {
  category: AllCategory;
  data: Record<string, string>;
  acknowledgement: {
    confirmed: boolean;
    signedName: string;
  };
}

export interface HealthUpdateListResponse {
  updates: HealthUpdate[];
}

export interface HealthUpdateAuditEntry {
  timestamp: string;
  action: 'added' | 'edited' | 'deleted' | 'archived';
  performedBy: string;
  updateId: string;
  category: AllCategory;
  data: Record<string, string>;
  previousData?: Record<string, string>;
}
